import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Search, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AssetLabelsPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/assets?limit=500${search ? `&search=${encodeURIComponent(search)}` : ''}`, { headers: authHeader() });
      setAssets(r.data.assets || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const print = async () => {
    if (selected.size === 0) { toast.error('Pick at least one asset'); return; }
    setBusy(true);
    try {
      const ids = Array.from(selected).join(',');
      const r = await axios.get(`${API_URL}/api/assets/labels.pdf?ids=${ids}`,
                                { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asset-labels-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Generated PDF with ${selected.size} label${selected.size !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error('Label generation failed');
    } finally { setBusy(false); }
  };

  const toggleAll = (e) => setSelected(e.target.checked ? new Set(assets.map(a => a.id)) : new Set());
  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="asset-labels-page">
      <Link to="/assets" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Workspace
      </Link>
      <div className="flex items-center justify-between mt-2 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]">Print labels</h1>
          <p className="text-sm text-[#64748B]">Select assets to generate a sheet of QR + Code-128 labels (3 columns × 10 rows per US Letter page).</p>
        </div>
        <Button onClick={print} disabled={busy || selected.size === 0} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="generate-labels-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
          Generate PDF ({selected.size})
        </Button>
      </div>

      <Card className="border-[#E2E8F0]">
        <div className="p-3 border-b border-[#E2E8F0] flex items-center gap-2">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <Input placeholder="Search by tag / name / serial…" value={search} onChange={e => setSearch(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && load()} className="h-8 text-sm flex-1" data-testid="labels-search" />
          <Button size="sm" variant="outline" onClick={load}>Search</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(assets.map(a => a.id)))} data-testid="select-all-btn">Select all ({assets.length})</Button>
          {selected.size > 0 && <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>}
        </div>
        {loading ? <div className="p-8 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          : assets.length === 0 ? <div className="p-8 text-center text-[#64748B]">No assets.</div>
          : (
          <ul className="divide-y divide-[#F1F5F9] max-h-[60vh] overflow-y-auto">
            {assets.map(a => (
              <li key={a.id} className="flex items-center gap-3 px-4 py-2 hover:bg-[#F8FAFC] cursor-pointer"
                  onClick={() => toggle(a.id)} data-testid={`label-row-${a.id}`}>
                <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} onClick={e => e.stopPropagation()} />
                <span className="font-mono text-xs text-[#0E7490] w-32">{a.asset_tag}</span>
                <span className="flex-1 text-sm text-[#0A2342]">{a.name}</span>
                {a.category_name && <Badge variant="outline" className="text-xs">{a.category_name}</Badge>}
                {a.serial_number && <span className="text-xs text-[#94A3B8] font-mono">S/N {a.serial_number}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
